import React, { useState } from 'react';
import { useBrandById } from '../store-hybrid';

interface BrandLogoProps {
  brandId: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export const BrandLogo: React.FC<BrandLogoProps> = ({ brandId, size = 'md', className = '' }) => {
  const brand = useBrandById(brandId);
  const [imageError, setImageError] = useState(false);
  
  const sizeClasses = {
    sm: 'w-6 h-6',
    md: 'w-8 h-8',
    lg: 'w-12 h-12'
  };

  const logoClass = `${sizeClasses[size]} ${className} rounded-lg bg-white border border-gray-200 p-1`;

  if (brand?.logo && brand.logo.startsWith('http') && !imageError) {
    return (
      <img 
        src={brand.logo} 
        alt={`${brand.name} logo`}
        className={`${logoClass} object-contain`}
        onError={() => setImageError(true)}
      />
    );
  }

  // Fallback to custom SVG logos
  return <FallbackLogo brandId={brandId} className={logoClass} />;
};

const FallbackLogo: React.FC<{ brandId: string; className: string }> = ({ brandId, className }) => {
  switch (brandId) {
    case 'vw':
      return (
        <div className={`${className} bg-blue-600 flex items-center justify-center`}>
          <svg viewBox="0 0 100 100" className="w-3/4 h-3/4 fill-white">
            <circle cx="50" cy="50" r="48" fill="none" stroke="white" strokeWidth="4"/>
            <path d="M25 35 L35 65 L42 65 L50 42 L58 65 L65 65 L75 35 L68 35 L61 58 L54 35 L46 35 L39 58 L32 35 Z"/>
          </svg>
        </div>
      );

    case 'audi':
      return (
        <div className={`${className} bg-gray-800 flex items-center justify-center`}>
          <svg viewBox="0 0 100 100" className="w-4/5 h-4/5 fill-none stroke-white" strokeWidth="3">
            <circle cx="20" cy="50" r="15"/>
            <circle cx="35" cy="50" r="15"/>
            <circle cx="65" cy="50" r="15"/>
            <circle cx="80" cy="50" r="15"/>
          </svg>
        </div>
      );

    case 'seat':
      return (
        <div className={`${className} bg-red-600 flex items-center justify-center`}>
          <svg viewBox="0 0 100 100" className="w-3/4 h-3/4 fill-white">
            <path d="M20 30 Q20 20 30 20 L70 20 Q80 20 80 30 L80 45 Q80 50 75 50 L25 50 Q20 50 20 45 Z"/>
            <path d="M25 55 L75 55 Q80 55 80 60 L80 70 Q80 80 70 80 L30 80 Q20 80 20 70 L20 60 Q20 55 25 55 Z"/>
            <text x="50" y="40" textAnchor="middle" fontSize="12" fill="white" fontWeight="bold">S</text>
            <text x="50" y="72" textAnchor="middle" fontSize="10" fill="white">EAT</text>
          </svg>
        </div>
      );

    case 'skoda':
      return (
        <div className={`${className} bg-green-600 flex items-center justify-center`}>
          <svg viewBox="0 0 100 100" className="w-3/4 h-3/4 fill-white">
            <circle cx="50" cy="50" r="45" fill="none" stroke="white" strokeWidth="4"/>
            <path d="M30 35 Q50 25 70 35 Q65 50 50 55 Q35 50 30 35 Z"/>
            <path d="M35 65 Q50 75 65 65 Q60 50 50 45 Q40 50 35 65 Z"/>
          </svg>
        </div>
      );

    case 'renault':
      return (
        <div className={`${className} bg-yellow-500 flex items-center justify-center`}>
          <svg viewBox="0 0 100 100" className="w-3/4 h-3/4 fill-white">
            <path d="M50 15 L35 35 L35 65 L50 85 L65 65 L65 35 Z"/>
            <path d="M50 25 L55 35 L55 65 L50 75 L45 65 L45 35 Z" fill="rgba(255,255,255,0.7)"/>
          </svg>
        </div>
      );

    case 'fiat':
      return (
        <div className={`${className} bg-red-700 flex items-center justify-center`}>
          <svg viewBox="0 0 100 100" className="w-3/4 h-3/4 fill-white">
            <rect x="20" y="35" width="60" height="30" rx="5"/>
            <text x="50" y="55" textAnchor="middle" fontSize="16" fill="white" fontWeight="bold">FIAT</text>
          </svg>
        </div>
      );

    case 'peugeot':
      return (
        <div className={`${className} bg-blue-800 flex items-center justify-center`}>
          <svg viewBox="0 0 100 100" className="w-3/4 h-3/4 fill-white">
            <path d="M30 25 Q50 15 70 25 L65 40 Q50 30 35 40 Z"/>
            <path d="M35 45 Q50 35 65 45 L60 60 Q50 50 40 60 Z"/>
            <path d="M40 65 Q50 55 60 65 L55 75 Q50 70 45 75 Z"/>
            <circle cx="45" cy="35" r="2"/>
          </svg>
        </div>
      );

    case 'geely':
      return (
        <div className={`${className} bg-blue-900 flex items-center justify-center`}>
          <svg viewBox="0 0 100 100" className="w-3/4 h-3/4 fill-white">
            <circle cx="50" cy="50" r="40" fill="none" stroke="white" strokeWidth="3"/>
            <path d="M30 40 Q50 30 70 40 Q50 50 30 40"/>
            <path d="M30 60 Q50 70 70 60 Q50 50 30 60"/>
            <circle cx="50" cy="50" r="5"/>
          </svg>
        </div>
      );

    case 'jetour':
      return (
        <div className={`${className} bg-orange-600 flex items-center justify-center`}>
          <svg viewBox="0 0 100 100" className="w-3/4 h-3/4 fill-white">
            <path d="M25 30 L75 30 L70 50 L30 50 Z"/>
            <path d="M30 55 L70 55 L75 70 L25 70 Z"/>
            <circle cx="40" cy="40" r="3"/>
            <circle cx="60" cy="62" r="3"/>
          </svg>
        </div>
      );

    default:
      return (
        <div className={`${className} bg-gray-400 flex items-center justify-center`}>
          <svg viewBox="0 0 24 24" className="w-3/4 h-3/4 fill-white">
            <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9L18 10c-.1-.1-.4-.6-1.4-1.3C16.1 8.4 14.7 8 13 8H9.5c-.6 0-1.1.4-1.4.9L6.8 11H4c-.6 0-1 .4-1 1v3c0 .6.4 1 1 1h2c0 1.7 1.3 3 3 3s3-1.3 3-3h4c0 1.7 1.3 3 3 3s3-1.3 3-3zM7.5 16C6.7 16 6 15.3 6 14.5S6.7 13 7.5 13s1.5.7 1.5 1.5S8.3 16 7.5 16zm9 0c-.8 0-1.5-.7-1.5-1.5s.7-1.5 1.5-1.5 1.5.7 1.5 1.5-.7 1.5-1.5 1.5z"/>
          </svg>
        </div>
      );
  }
};
